/*
    # PantaForm

    *** Recommended usage ***
    Handle state and validation for each step in your step component.
    When changing a value in your step component state, also call
    onChange prop function. By doing this, the form keeps track of
    the combined state of the whole form.

    *** Props ***
    className:          Custom class names for the form wrapper.
    defaultValues:      Object containing default values for the whole form.
	hideStepIndicator: 	Hide the step indicator.
    initialStep: 		Start on this step when opening the form. Default is 1.
    onCancel:           Handle to call when user cancels form.
	onChange: 			Possibility to keep track of all values outside of the form.
    onSubmit: 			When onSubmit is called inside a form step, this will
                        call prop onSubmit with all form values as argument.
                        Given that you use onChange on value changes.
    onChangeStep        Callback for when step is changed

    Only child components of type PantaFormStep will be switched when changing step.
    All other children will be rendered inside the ExtraContent container.

	Step components should inherit type props from PantaFormStepPassedProps.
*/

import React, {
    useEffect,
    useState,
    FunctionComponent,
    ReactElement,
} from 'react';
import StepIndicator from '../StepIndicator';
import {PantaFormProps, PantaFormStepPassedProps} from './types';
import {countNrOfSteps, isChildFormStep} from './utils';
import './Scss/panta-form.scss';

const PantaForm: FunctionComponent<PantaFormProps> = props => {
    const [step, setStep] = useState<number>(props.initialStep || 1);
    const [values, setValues] = useState<object>(props.defaultValues || {});
    const nrOfSteps = countNrOfSteps(React.Children, props);

    // If initialStep or defaultValues is changed from outside
    // of the form, update them, just to be sure.
    useEffect(() => {
        props.initialStep && setStep(props.initialStep)
        props.defaultValues && setValues(props.defaultValues)
    }, [props.initialStep, props.defaultValues]);

    const nextStep = () => {
        props.onChangeStep && props.onChangeStep();

        setStep(step + 1);
    }

    const GoToStep = (stepToNavigateTo: number) => {
        props.onChangeStep && props.onChangeStep();

        setStep(stepToNavigateTo);
    }

    const prevStep = () => {
        props.onChangeStep && props.onChangeStep();

        setStep(step - 1);
    }

    const resetValues = () => {
        setValues(props.defaultValues || {});
    }

    const onCancel = () => {
        props.onCancel && props.onCancel();
    }

    const onChangeValues = (changedValues: any) => {
        const updatedValues = {...values, ...changedValues};
        setValues(updatedValues);
        props.onChange && props.onChange(updatedValues);
    }

    const onSubmit = () => {
        props.onSubmit(values);
    }

    const renderActiveStep = () => {
        if (!props.children) {
            return null;
        }

        let countSteps = 0;

        return React.Children.map(props.children, (child: ReactElement) => {
            if (isChildFormStep(child)) {
                countSteps += 1;
                const passedProps: PantaFormStepPassedProps = {
                    prevStep: prevStep,
                    nextStep: nextStep,
                    goToStep: GoToStep,
                    onCancel: onCancel,
                    onChangeValues: onChangeValues,
                    onSubmit: onSubmit,
                    resetValues: resetValues,
                    values: values,
                }
                if (countSteps === step) {
                    return React.cloneElement(child, passedProps)
                }
            }
            return <></>;
        })
    }

    const renderExtraContent = () => {
        if (!props.children) {
            return null;
        }

        return React.Children.map(props.children, (child: ReactElement) => {
            if (!isChildFormStep(child)) {
                return child
            }
            return <></>
        });
    }

    return (
        <div className={`panta-form ${props.className ?? ""}`}>
            {!props.hideStepIndicator && (
                <div className='panta-form__step-indicator'>
                    <StepIndicator step={step} count={nrOfSteps}/>
                </div>
            )}
            <div className='panta-form__step'>
                {renderActiveStep()}
            </div>
            <div className='panta-form__extra-content'>
                {renderExtraContent()}
            </div>
        </div>
    );
}

export default PantaForm;